import React, { useContext, useState } from 'react';

import {
  getOnboardingUrl,
  getOnboardingTransitionUrl,
  toast,
  useRequest,
  OnboardingDto,
  ONBOARDING_STAGES,
  SurveyDto,
} from '@monument/core';
import { hasActiveOnboarding } from './onboarding.utils';
import { Result } from 'ahooks/lib/useRequest/src/types';
import honeybadger from '@honeybadger-io/js/dist/browser/honeybadger';
import { AxiosError } from 'axios';

type OnboardingTransitionEvent = string;
export interface OnboardingTransitionPayload extends Record<string, unknown> {}
interface OnboardingTransitionOptions {
  /**
   * Transitions may mutate other application data. Allow consumers to provide
   * an onBeforeSync callback for any necessary cache-busting (or other followups)
   * before mutating the client's source-of-truth onboarding object.
   * @param newOnboardingData
   * @returns
   */
  onBeforeSync?: ((newOnboardingData: OnboardingData) => Promise<unknown>) | (() => void);

  /**
   * This is for logic such as navigating to the correct page (e.g. '/onboarding'
   * or '/dashboard') after running a transition and having the client’s
   * onboarding object up to date.
   * @param newOnboardingData
   * @returns
   */
  onAfterSync?: ((newOnboardingData: OnboardingData) => Promise<unknown>) | (() => void);
  handleErrors?: boolean;
}

export interface OnboardingData extends OnboardingDto {
  intakeSurvey: SurveyDto;
}

interface OnboardingContextValue {
  onboardingReq: Result<unknown, []>;
  onboarding: OnboardingData | undefined;
  transition: (
    event: OnboardingTransitionEvent,
    payload?: OnboardingTransitionPayload,
    options?: OnboardingTransitionOptions,
  ) => Promise<OnboardingData>;
  transitionLoading: boolean;
  hasActiveOnboarding: boolean;
  hasCompletedOnboarding: boolean;
}

const OnboardingContext = React.createContext<OnboardingContextValue>({} as OnboardingContextValue);
export const useOnboarding = () => useContext(OnboardingContext);

export const OnboardingContextProvider = ({ children }) => {
  const [transitionLoading, setTransitionLoading] = useState<boolean>(false);

  const onboardingReq = useRequest<OnboardingData | undefined>(
    {
      url: getOnboardingUrl(),
      withErrorsHandler: false,
    },
    {
      manual: true,
    },
  );

  const transitionReq = useRequest<
    OnboardingData,
    [
      OnboardingTransitionEvent,
      OnboardingTransitionPayload | undefined,
      OnboardingTransitionOptions | undefined,
    ]
  >(
    (
      event: OnboardingTransitionEvent,
      payload?: OnboardingTransitionPayload,
      options?: OnboardingTransitionOptions,
    ) => ({
      url: getOnboardingTransitionUrl(),
      method: 'post',
      data: {
        event,
        payload,
      },
      withErrorsHandler: options?.handleErrors ?? true,
    }),
    {
      manual: true,
      onError: (error, [transitionEvent, transitionPayload, transitionOptions]) => {
        if (transitionOptions?.handleErrors) {
          // Invalid state transitions shouldn't be possible if everything is working correctly. On the
          // off-chance a user gets into an invalid state, we should restore to whatever stage the
          // backend thinks we should be in.
          honeybadger.notify(error as AxiosError, {
            message: `useOnboarding#transition error during ${transitionEvent} using ${transitionPayload}`,
          });
          onboardingReq.refresh();
          toast.error({
            title: 'Something went wrong',
            description:
              'Please reload the page and try again, or contact support if the problem continues.',
          });
        }
      },
    },
  );

  /**
   * This is always used instead of transitionReq.run() so we can ensure
   * onboarding.stage is up to date along with the browser route when making a
   * transition while also navigating to a different page.
   */
  const transition = async (
    event: OnboardingTransitionEvent,
    payload?: OnboardingTransitionPayload,
    options?: OnboardingTransitionOptions,
  ) => {
    setTransitionLoading(true);
    try {
      const newOnboarding = await transitionReq.runAsync(event, payload, options);
      // Transitions may mutate other application data. Allow consumers to provide an onBeforeSync
      // callback for any necessary cache-busting (or other followups) before mutating the
      // client's source-of-truth onboarding object.
      if (options?.onBeforeSync) {
        await options.onBeforeSync(newOnboarding);
      }
      onboardingReq.mutate(newOnboarding);
      // This is for logic such as navigating to the correct page (e.g. '/onboarding' or '/dashboard')
      // after running a transition and having the client’s onboarding object up to date.
      if (options?.onAfterSync) {
        await options.onAfterSync(newOnboarding);
      }
      return newOnboarding;
    } finally {
      setTransitionLoading(false);
    }
  };

  return (
    <OnboardingContext.Provider
      value={{
        onboardingReq,
        onboarding: onboardingReq.data,
        transition,
        transitionLoading,
        hasActiveOnboarding: hasActiveOnboarding(onboardingReq.data),
        hasCompletedOnboarding:
          onboardingReq.data?.stage === ONBOARDING_STAGES.EXITED &&
          onboardingReq.data?.store.previous === ONBOARDING_STAGES.COMPLETE,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};
